import { TaskConflict } from 'src/services/jmr/types';
import { MeasureUnitValues } from 'src/services/tasks/constants';
import { setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { JmrTask } from 'src/store/newJmrPageSlice/types';
import { AppDispatch } from 'src/store/store';
import { TableDataRow } from 'src/utils/jmrUtils';

/**
 * Check or uncheck a job placement and return a new job results array
 */
export const toggleJob = (id: string, jmrResults: TableDataRow[], dispatch: AppDispatch) => {
  return dispatch(
    setJmrResults(
      jmrResults.map((r) =>
        r.id === id
          ? {
              ...r,
              isChecked: !r.isChecked,
              // set total hours to 0 if it's currently checked (meaning we are unchecking)
              totalHours: r.isChecked ? 0 : r.totalHours,
            }
          : r,
      ),
    ),
  );
};

/**
 *  Calculate the required hours for a given task in a single placement row (legacy)
 * NOTE: this method is dependent on a call to getJob when the job is selected which populates the row's jobRequirements.
 *  Said call is only made in the pre-modifications (legacy) experience
 */
export const calculatePlacementHours = (row: TableDataRow, task: JmrTask) => {
  // get the percentage from the job requirements
  const requirmentValues = row.jobRequirements?.filter((t) => t.task.id === task.taskId) ?? [];
  // multiply by the assigned hours of each job
  const assignedValue = requirmentValues?.length > 0 ? +(+requirmentValues[0].value * +row.totalHours).toFixed(1) : 0;
  return assignedValue;
};

/**
 *  Calculate the total required hours for a given task across all placements (legacy)
 * NOTE: this method is dependent on a call to getJob when the job is selected which populates the row's jobRequirements.
 *  Said call is only made in the pre-modifications (legacy) experience
 */
export const calculateTotalHours = (task: JmrTask, selectedResults: TableDataRow[]) => {
  return selectedResults.length > 0
    ? +selectedResults
        .map((r) => calculatePlacementHours(r, task))
        .reduce((prev, curr) => prev + curr)
        .toFixed(1)
    : 0;
};

/**
 *  Calculate the unused hours in a task
 */
export const calculateUnusedHoursForTask = (task: JmrTask, selectedResults: TableDataRow[]) => {
  return +(+task.restrictedValue - calculateTotalHours(task, selectedResults)).toFixed(1);
};

/**
 *  Calculate unused total hours for a table
 */
export const calculateUnusedTotalHours = (
  selectedResults: TableDataRow[],
  shiftLength: number,
  // TODO limitedHoursPerDay should be string | undefined and converted to number in this method instead of during call
  limitedHoursPerDay: number,
) => {
  const totalHoursSum =
    selectedResults.length > 0 ? selectedResults.map((r) => +r.totalHours).reduce((prev, curr) => prev + curr) : 0;
  return (limitedHoursPerDay ? limitedHoursPerDay : shiftLength) - totalHoursSum;
};

/**
 *  Validate total unused hours based on the contraints in background page
 */
// TODO limitedHoursPerDay should be string | undefined and converted to number in this method instead of during call
export const isTotalUnusedHoursValid = (totalUnusedHours: number, shiftLength: number, limitedHoursPerDay?: number) => {
  const totalCap = limitedHoursPerDay ? limitedHoursPerDay : shiftLength;
  return totalUnusedHours >= 0 && totalUnusedHours < totalCap;
};

export const areAllUsedHoursMoreThanZero = (selectedJobs: TableDataRow[]) => {
  return selectedJobs.every((selectedJob) => +selectedJob.totalHours > 0);
};

/** Returns the number of hours a given conflicting requirement would need to be performed base on the hours allocated to
 * the job. NOTE this function is meant for checking thresholds, values are not suitable for display, caller should use toFixed */
export function getRequiredTaskHours(jobMatch: TableDataRow, conflict: TaskConflict) {
  if (+jobMatch.totalHours) {
    return +conflict.requirementProportion * +jobMatch.totalHours;
  }
  return 0;
}

export function getRequiredTaskPercent(conflict: TaskConflict) {
  return +conflict.requirementProportion * 100;
}

/** Returns a string outlining the amount of a shift the conflict task is required for (in hours if any are allocated) */
export function getRequirementQuantity(jobMatch: TableDataRow, conflict: TaskConflict) {
  const taskHours = getRequiredTaskHours(jobMatch, conflict);
  return taskHours ? `${taskHours.toFixed(1)} hours` : `${getRequiredTaskPercent(conflict).toFixed(1)}%`;
}

function getDefaultTaskAccumulator(conflict: TaskConflict) {
  const { taskId, taskName } = conflict;
  const contributingJobs: { id: string; name: string }[] = []; // setup type hint for ReturnType<>
  const restrictedHours = conflict.measureUnitType != MeasureUnitValues.Bool ? +conflict.restrictedValue : 0;

  return {
    taskId,
    taskName,
    restrictedHours,
    totalHours: 0,
    contributingJobs,
  };
}
// Map.get always has `any` type, use this definition to fix that
export type TaskTotalAndJobs = ReturnType<typeof getDefaultTaskAccumulator>;

export function getRequirementSumsAndJobs(selectedJobs: TableDataRow[]) {
  const accumulatedMap = selectedJobs.reduce((accumulator, sj) => {
    if (!sj.conflicts?.length) {
      return accumulator;
    } // exclude jobs with no conflicts

    return sj.conflicts.reduce((accumulator, c) => {
      if (c.appliedModifications?.length) {
        return accumulator;
      } // exclude modified conflicts

      if (!c.requirementProportion) {
        console.warn(
          "Job '%s' task '%s' is missing requirementProportion. The job may be misconfigured or there may be a problem in fetching this information.",
          sj.name,
          c.taskName,
        );
        return accumulator;
      }

      if (!accumulator.has(c.taskId)) {
        accumulator.set(c.taskId, getDefaultTaskAccumulator(c)); // setup initial task accumulator
      }
      const taskLevelAccumulator = accumulator.get(c.taskId)!; // always set per above
      // Update by reference
      const requiredTaskHours = getRequiredTaskHours(sj, c);
      if (requiredTaskHours) {
        taskLevelAccumulator.totalHours += requiredTaskHours;
        const { id, name } = sj;
        taskLevelAccumulator.contributingJobs.push({ id, name });
      }

      return accumulator;
    }, accumulator);
  }, new Map<number, TaskTotalAndJobs>());

  return Array.from(accumulatedMap.values());
}

export const getRequirementColor = (
  { taskId, appliedModifications }: TaskConflict,
  exceededRestrictionIds: Set<number>,
) => (!appliedModifications?.length && exceededRestrictionIds.has(taskId) ? 'error' : undefined);
